<template>
  <div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center flex-wrap">
            <h4 class="mb-0">
              {{ $t('priceSchedule.title') }}<span v-if="experienceName">: {{ experienceName }}</span>
            </h4>
            <CButton color="secondary" variant="outline" size="sm" class="my-1" @click="backToExperience">
              <i class="fas fa-arrow-left mr-1"></i> {{ $t('priceSchedule.backToExperience') }}
            </CButton>
          </div>
          <div class="card-body">
            <!-- Month navigation -->
            <div class="schedule-toolbar">
              <div class="d-flex align-items-center">
                <CButton color="secondary" variant="outline" class="nav-button" :disabled="!canGoBack" @click="moveMonth(-1)" :aria-label="$t('priceSchedule.previousMonth')">
                  <i class="fas fa-chevron-left"></i>
                </CButton>
                <h3 class="month-title mb-0">{{ monthTitle }}</h3>
                <CButton color="secondary" variant="outline" class="nav-button" :disabled="!canGoForward" @click="moveMonth(1)" :aria-label="$t('priceSchedule.nextMonth')">
                  <i class="fas fa-chevron-right"></i>
                </CButton>
              </div>
              <div class="d-flex align-items-center">
                <select class="form-control month-select" :value="monthKey(current)" @change="jumpTo($event.target.value)" :aria-label="$t('priceSchedule.jumpTo')">
                  <optgroup v-for="group in monthOptionGroups" :key="group.year" :label="group.year">
                    <option v-for="option in group.months" :key="option.key" :value="option.key">{{ option.label }}</option>
                  </optgroup>
                </select>
                <CButton color="primary" class="ml-2" @click="jumpTo(monthKey(firstMonth))">{{ $t('priceSchedule.today') }}</CButton>
              </div>
            </div>

            <div v-if="error" class="alert alert-danger">{{ error }}</div>

            <div class="row">
              <!-- Month grid -->
              <div class="col-xl-8 mb-4">
                <div class="calendar" :class="{ 'is-loading': loading }">
                  <div class="calendar-weekdays">
                    <div v-for="weekday in weekdays" :key="weekday" class="calendar-weekday">{{ weekday }}</div>
                  </div>
                  <div class="calendar-grid">
                    <template v-for="(cell, index) in cells">
                      <div v-if="!cell" :key="'empty-' + index" class="calendar-cell is-empty"></div>
                      <button
                        v-else
                        :key="cell.iso"
                        type="button"
                        class="calendar-cell"
                        :class="{
                          'is-period': cell.day && cell.day.source === 'period',
                          'is-past': cell.iso < todayIso,
                          'is-today': cell.iso === todayIso,
                          'is-selected': cell.iso === selectedIso
                        }"
                        :style="cellStyle(cell)"
                        @click="selectedIso = cell.iso">
                        <span class="cell-header">
                          <span class="cell-day">{{ cell.dayNumber }}</span>
                          <span v-if="cell.day && cell.day.slotCount > 0"
                                class="cell-slots"
                                :class="{ 'is-full': !cell.day.availableSlotCount }"
                                :title="slotsText(cell.day)">
                            <span class="slot-dot"></span>{{ cell.day.availableSlotCount }}
                          </span>
                        </span>
                        <span v-if="cell.day" class="cell-price">{{ formatEuroShort(cell.day.displayPrice) }}</span>
                        <span class="cell-period">{{ cell.day && cell.day.source === 'period' ? cell.day.pricePeriodName : '\u00a0' }}</span>
                      </button>
                    </template>
                  </div>
                  <div v-if="loading" class="calendar-loading">
                    <CSpinner color="primary" />
                  </div>
                </div>
              </div>

              <!-- Legend and day details -->
              <div class="col-xl-4">
                <div class="side-panel mb-4">
                  <h6 class="side-title">{{ $t('priceSchedule.legend') }}</h6>
                  <ul class="legend">
                    <li class="legend-item">
                      <span class="legend-swatch is-base"></span>
                      <span class="legend-text">
                        <strong>{{ $t('priceSchedule.basePrice') }}</strong>
                        <small class="d-block text-muted">{{ $t('priceSchedule.basePriceHint') }}</small>
                      </span>
                      <span class="legend-price">{{ formatEuro(baseDisplayPrice) }}</span>
                    </li>
                    <li v-for="(period, index) in periods" :key="period.id" class="legend-item">
                      <span class="legend-swatch" :style="{ background: colorOf(index).tint, borderColor: colorOf(index).solid }"></span>
                      <span class="legend-text">
                        <strong>{{ period.name }}</strong>
                        <small class="d-block text-muted">{{ periodDates(period) }}</small>
                      </span>
                      <span class="legend-price">{{ formatEuro(periodDisplayPrice(period)) }}</span>
                    </li>
                    <li class="legend-item legend-slots">
                      <span class="cell-slots"><span class="slot-dot"></span>2</span>
                      <span class="legend-text"><small class="text-muted">{{ $t('priceSchedule.slotsLegend') }}</small></span>
                    </li>
                  </ul>
                  <p v-if="priceType === 'PerPerson'" class="small text-muted mb-0">{{ $t('priceSchedule.perPersonHint') }}</p>
                </div>

                <div class="side-panel">
                  <h6 class="side-title">{{ $t('priceSchedule.dayDetails') }}</h6>
                  <div v-if="!selectedDay" class="text-muted small">{{ $t('priceSchedule.selectDay') }}</div>
                  <div v-else>
                    <div class="detail-date">{{ longDate(selectedDay.date) }}</div>
                    <div class="mb-3">
                      <span v-if="selectedDay.source === 'period'"
                            class="source-badge"
                            :style="{ background: periodColorById(selectedDay.pricePeriodId).tint, color: periodColorById(selectedDay.pricePeriodId).solid }">
                        {{ $t('priceSchedule.sourcePeriod') }}: {{ selectedDay.pricePeriodName }}
                      </span>
                      <span v-else class="source-badge is-base">{{ $t('priceSchedule.basePrice') }}</span>
                    </div>
                    <table class="table table-sm detail-table mb-2">
                      <tbody>
                        <template v-if="priceType === 'PerPerson' && selectedDayBrackets.length">
                          <tr v-for="bracket in selectedDayBrackets" :key="bracket.key">
                            <td>{{ bracket.label }}</td>
                            <td class="text-right"><strong>{{ formatBracketPrice(bracket.price) }}</strong></td>
                          </tr>
                        </template>
                        <tr v-else>
                          <td>{{ priceType === 'PerPerson' ? $t('experiences.priceTypePerPerson') : $t('experiences.priceTypePerExperience') }}</td>
                          <td class="text-right"><strong>{{ formatEuro(selectedDay.price) }}</strong></td>
                        </tr>
                        <tr>
                          <td>{{ $t('priceSchedule.slots') }}</td>
                          <td class="text-right">{{ slotsText(selectedDay) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { pricePeriodsService, pricePeriodErrorMessages } from '@/services/pricePeriods.service';
import { experiencesService } from '@/services/experiences.service';
import { periodColor, toIsoDate, formatDateDMY, formatEuro, formatEuroShort, formatBracketPrice, displayPrice, pricedAgeBrackets, effectiveAgeBracketPrices } from '@/helpers/prices';

// How far ahead the schedule can be browsed
const MONTHS_AHEAD = 24;

function firstOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

export default {
  name: 'ExperiencePriceSchedule',
  data() {
    const firstMonth = firstOfMonth(new Date());
    return {
      firstMonth,
      current: firstMonth,
      todayIso: toIsoDate(new Date()),
      selectedIso: null,
      experienceName: '',
      priceType: 'PerExperience',
      basePrice: 0,
      baseAgeBracketPrices: {},
      periods: [],
      daysByMonth: {},
      loading: false,
      error: ''
    };
  },
  computed: {
    experienceId() {
      return this.$route.query.id;
    },
    locale() {
      return this.$i18n.locale === 'en' ? 'en-GB' : 'pt-PT';
    },
    lastMonth() {
      return addMonths(this.firstMonth, MONTHS_AHEAD);
    },
    canGoBack() {
      return this.current > this.firstMonth;
    },
    canGoForward() {
      return this.current < this.lastMonth;
    },
    monthTitle() {
      const title = this.current.toLocaleDateString(this.locale, { month: 'long', year: 'numeric' });
      return title.charAt(0).toUpperCase() + title.slice(1);
    },
    monthOptionGroups() {
      const groups = [];
      for (let i = 0; i <= MONTHS_AHEAD; i++) {
        const month = addMonths(this.firstMonth, i);
        const year = String(month.getFullYear());
        let group = groups.find(g => g.year === year);
        if (!group) {
          group = { year, months: [] };
          groups.push(group);
        }
        const label = month.toLocaleDateString(this.locale, { month: 'long', year: 'numeric' });
        group.months.push({ key: this.monthKey(month), label: label.charAt(0).toUpperCase() + label.slice(1) });
      }
      return groups;
    },
    weekdays() {
      // Monday first; 2024-01-01 was a Monday
      const names = [];
      for (let i = 0; i < 7; i++) {
        const name = new Date(2024, 0, 1 + i).toLocaleDateString(this.locale, { weekday: 'short' }).replace('.', '');
        names.push(name.charAt(0).toUpperCase() + name.slice(1));
      }
      return names;
    },
    currentDays() {
      return this.daysByMonth[this.monthKey(this.current)] || {};
    },
    cells() {
      const year = this.current.getFullYear();
      const month = this.current.getMonth();
      const leading = (new Date(year, month, 1).getDay() + 6) % 7;
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const cells = [];
      for (let i = 0; i < leading; i++) cells.push(null);
      for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber++) {
        const iso = toIsoDate(new Date(year, month, dayNumber));
        cells.push({ iso, dayNumber, day: this.currentDays[iso] || null });
      }
      while (cells.length % 7 !== 0) cells.push(null);
      return cells;
    },
    selectedDay() {
      if (!this.selectedIso) return null;
      const month = this.selectedIso.substring(0, 7);
      const days = this.daysByMonth[month];
      return days ? days[this.selectedIso] || null : null;
    },
    selectedDayBrackets() {
      if (!this.selectedDay) return [];
      // Brackets the period leaves out keep the base price
      const prices = this.selectedDay.source === 'period'
        ? effectiveAgeBracketPrices(this.baseAgeBracketPrices, this.selectedDay.ageBracketPrices)
        : effectiveAgeBracketPrices(this.selectedDay.ageBracketPrices, null);
      return pricedAgeBrackets(prices);
    },
    baseDisplayPrice() {
      return displayPrice(this.priceType, this.basePrice, this.baseAgeBracketPrices);
    },
    periodIndexById() {
      const indexById = {};
      this.periods.forEach((period, index) => { indexById[period.id] = index; });
      return indexById;
    }
  },
  watch: {
    current() {
      this.loadMonth();
    },
    // The view is reused when only ?id= changes: start over for the new experience
    experienceId() {
      this.loadAll();
    }
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    formatEuro,
    formatEuroShort,
    formatBracketPrice,
    monthKey(date) {
      return toIsoDate(date).substring(0, 7);
    },
    colorOf(index) {
      return periodColor(index);
    },
    periodColorById(id) {
      const index = this.periodIndexById[id];
      return periodColor(index === undefined ? 0 : index);
    },
    periodDates(period) {
      const from = formatDateDMY(period.validFrom);
      const to = formatDateDMY(period.validTo);
      return from === to ? from : `${from} – ${to}`;
    },
    periodDisplayPrice(period) {
      return displayPrice(this.priceType, period.price, effectiveAgeBracketPrices(this.baseAgeBracketPrices, period.ageBracketPrices));
    },
    cellStyle(cell) {
      if (!cell.day || cell.day.source !== 'period') return null;
      const color = this.periodColorById(cell.day.pricePeriodId);
      return { background: color.tint, borderTopColor: color.solid };
    },
    slotsText(day) {
      if (!day.slotCount) return this.$t('priceSchedule.noSlots');
      return this.$t('priceSchedule.slotsSummary', { total: day.slotCount, available: day.availableSlotCount });
    },
    longDate(value) {
      const [year, month, day] = toIsoDate(value).split('-').map(Number);
      const text = new Date(year, month - 1, day).toLocaleDateString(this.locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    moveMonth(offset) {
      const target = addMonths(this.current, offset);
      if (target >= this.firstMonth && target <= this.lastMonth) this.current = target;
    },
    jumpTo(key) {
      const [year, month] = key.split('-').map(Number);
      this.current = new Date(year, month - 1, 1);
    },
    backToExperience() {
      this.$router.push({ name: 'ExperiencesEdit', query: { id: this.experienceId } });
    },
    loadAll() {
      // Moving back to the first month triggers the `current` watcher, which loads it
      const monthChanges = this.current.getTime() !== this.firstMonth.getTime();
      this.current = this.firstMonth;
      this.selectedIso = null;
      this.experienceName = '';
      this.priceType = 'PerExperience';
      this.basePrice = 0;
      this.baseAgeBracketPrices = {};
      this.periods = [];
      this.daysByMonth = {};
      this.error = '';
      this.loadExperience();
      this.loadPeriods();
      if (!monthChanges) this.loadMonth();
    },
    async loadExperience() {
      const experienceId = this.experienceId;
      try {
        const response = await experiencesService.getById(experienceId);
        if (experienceId !== this.experienceId) return;
        const data = response && response.data ? response.data : response;
        if (data && data.names) {
          const raw = data.names[this.$i18n.locale] || data.names.pt || '';
          this.experienceName = String(raw).replace(/<[^>]*>/g, '').trim();
        }
      } catch (error) {
        // The name is only a title; the schedule still works without it
        console.error('Error loading experience:', error);
      }
    },
    async loadPeriods() {
      const experienceId = this.experienceId;
      try {
        const periods = await pricePeriodsService.getList(experienceId);
        if (experienceId !== this.experienceId) return;
        this.periods = (Array.isArray(periods) ? periods : [])
          .slice()
          .sort((a, b) => toIsoDate(a.validFrom).localeCompare(toIsoDate(b.validFrom)));
      } catch (error) {
        this.error = pricePeriodErrorMessages(error, this.$t('pricePeriods.loadError')).join(' ');
      }
    },
    async loadMonth() {
      const experienceId = this.experienceId;
      const key = this.monthKey(this.current);
      if (this.daysByMonth[key]) return;
      const from = toIsoDate(this.current);
      const to = toIsoDate(new Date(this.current.getFullYear(), this.current.getMonth() + 1, 0));
      this.loading = true;
      this.error = '';
      try {
        const schedule = await pricePeriodsService.getSchedule(experienceId, from, to);
        // Ignore a late answer for a previous experience
        if (!schedule || experienceId !== this.experienceId) return;
        this.priceType = schedule.priceType || this.priceType;
        this.basePrice = schedule.basePrice || 0;
        this.baseAgeBracketPrices = schedule.baseAgeBracketPrices || {};
        const days = {};
        (schedule.days || []).forEach(day => { days[toIsoDate(day.date)] = day; });
        this.$set(this.daysByMonth, key, days);
        if (!this.selectedIso || this.selectedIso.substring(0, 7) !== key) {
          this.selectedIso = key === this.monthKey(this.firstMonth) ? this.todayIso : from;
        }
      } catch (error) {
        this.error = pricePeriodErrorMessages(error, this.$t('priceSchedule.loadError')).join(' ');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.schedule-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.month-title {
  min-width: 16rem;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
  color: #3c4b64;
}

.nav-button {
  width: 2.5rem;
}

.month-select {
  width: auto;
  min-width: 12rem;
}

.calendar {
  position: relative;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.calendar.is-loading .calendar-grid {
  opacity: 0.4;
}

.calendar-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.calendar-weekdays,
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.calendar-weekdays {
  background: #f4f5f7;
  border-bottom: 1px solid #d8dbe0;
}

.calendar-weekday {
  padding: 0.6rem 0.25rem;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #768192;
}

.calendar-cell {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 92px;
  padding: 0.45rem 0.55rem;
  border: 0;
  border-top: 3px solid transparent;
  border-right: 1px solid #eceef1;
  border-bottom: 1px solid #eceef1;
  background: #fff;
  text-align: left;
  font: inherit;
  color: #3c4b64;
  cursor: pointer;
  transition: box-shadow 0.12s ease;
}

.calendar-cell:nth-child(7n) {
  border-right: 0;
}

.calendar-cell.is-empty {
  background: #fafbfc;
  cursor: default;
}

.calendar-cell:not(.is-empty):hover {
  box-shadow: inset 0 0 0 2px #c4c9d0;
}

.calendar-cell:focus {
  outline: none;
}

.calendar-cell.is-selected {
  box-shadow: inset 0 0 0 2px #3c4b64;
}

.calendar-cell.is-past {
  opacity: 0.55;
}

.cell-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cell-day {
  font-size: 0.85rem;
  font-weight: 600;
}

.calendar-cell.is-today .cell-day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  margin: -0.15rem 0 0 -0.25rem;
  border-radius: 50%;
  background: #3c4b64;
  color: #fff;
}

.cell-slots {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 600;
  color: #4d7a3a;
}

.cell-slots.is-full {
  color: #9da5b1;
}

.slot-dot {
  width: 7px;
  height: 7px;
  margin-right: 3px;
  border-radius: 50%;
  background: #6fa456;
}

.cell-slots.is-full .slot-dot {
  background: transparent;
  border: 1.5px solid #9da5b1;
}

.cell-price {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
}

.cell-period {
  overflow: hidden;
  font-size: 0.68rem;
  color: #636f83;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.side-panel {
  padding: 1rem 1.1rem;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  background: #fff;
}

.side-title {
  margin-bottom: 0.85rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #768192;
}

.legend {
  margin: 0 0 0.5rem;
  padding: 0;
  list-style: none;
}

.legend-item {
  display: flex;
  align-items: center;
  padding: 0.45rem 0;
  border-bottom: 1px solid #f0f1f3;
}

.legend-item:last-child {
  border-bottom: 0;
}

.legend-swatch {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-right: 0.75rem;
  border: 1px solid #d8dbe0;
  border-top-width: 4px;
  border-radius: 3px;
}

.legend-swatch.is-base {
  background: #fff;
}

.legend-slots .cell-slots {
  width: 22px;
  margin-right: 0.75rem;
}

.legend-text {
  flex: 1 1 auto;
  line-height: 1.25;
}

.legend-price {
  margin-left: 0.5rem;
  font-weight: 700;
  white-space: nowrap;
}

.detail-date {
  margin-bottom: 0.5rem;
  font-size: 1.05rem;
  font-weight: 600;
}

.source-badge {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.source-badge.is-base {
  background: #ebedef;
  color: #3c4b64;
}

.detail-table td {
  border-top: 1px solid #f0f1f3;
}

@media (max-width: 576px) {
  .calendar-cell {
    min-height: 64px;
    padding: 0.3rem;
  }

  .cell-price {
    font-size: 0.85rem;
  }

  .cell-period {
    display: none;
  }

  .month-title {
    min-width: 0;
    font-size: 1.1rem;
  }
}
</style>
